import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';

export interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string; // CSS selector for element to highlight
  route?: string; // Optional route to navigate to
  position: 'top' | 'bottom' | 'left' | 'right';
  action?: () => void; // Optional action to perform
}

export interface Tour {
  id: string;
  name: string;
  steps: TourStep[];
}

/**
 * Service to manage guided tours for first-time users
 * Stores tour completion status in localStorage
 */
@Injectable({
  providedIn: 'root'
})
export class TourService {
  private readonly STORAGE_KEY = 'qa-agent-tours-completed';
  private readonly TOUR_SKIPPED_KEY = 'qa-agent-tour-skipped';

  // Current tour state
  private currentTourSignal = signal<Tour | null>(null);
  private currentStepIndexSignal = signal(0);
  private isActiveSignal = signal(false);

  // Public computed values
  currentTour = this.currentTourSignal.asReadonly();
  currentStepIndex = this.currentStepIndexSignal.asReadonly();
  isActive = this.isActiveSignal.asReadonly();

  currentStep = computed(() => {
    const tour = this.currentTourSignal();
    const index = this.currentStepIndexSignal();
    return tour?.steps[index] || null;
  });

  isFirstStep = computed(() => this.currentStepIndexSignal() === 0);
  isLastStep = computed(() => {
    const tour = this.currentTourSignal();
    const index = this.currentStepIndexSignal();
    return tour ? index === tour.steps.length - 1 : false;
  });

  progress = computed(() => {
    const tour = this.currentTourSignal();
    const index = this.currentStepIndexSignal();
    return tour ? ((index + 1) / tour.steps.length) * 100 : 0;
  });

  constructor(private router: Router) {}

  /**
   * Define the main application tour
   */
  getMainTour(): Tour {
    return {
      id: 'main-tour',
      name: 'Getting Started with QA Agent',
      steps: [
        {
          id: 'welcome',
          title: 'Welcome to QA Agent! 👋',
          content: 'Let\'s take a quick tour to show you around. This will only take a minute.',
          target: '.dashboard-header',
          route: '/dashboard',
          position: 'bottom'
        },
        {
          id: 'upload',
          title: 'Upload Excel Files',
          content: 'Click here to upload your MI Excel workbooks for validation. We support all 9 standard MI sheets.',
          target: '.action-card.primary',
          position: 'bottom'
        },
        {
          id: 'rules',
          title: 'Manage Validation Rules',
          content: 'Configure and customize validation rules to match your organization\'s requirements.',
          target: '.action-card.secondary',
          position: 'bottom'
        },
        {
          id: 'ai-suggestions',
          title: 'AI-Powered Suggestions',
          content: 'Our AI analyzes validation patterns and suggests new rules to improve your quality assurance.',
          target: '.action-card.ai',
          position: 'bottom'
        },
        {
          id: 'history',
          title: 'View Validation History',
          content: 'Access past validation sessions, compare results, and track improvements over time.',
          target: '.view-all-link',
          position: 'left'
        },
        {
          id: 'complete',
          title: 'You\'re All Set! 🎉',
          content: 'You\'re ready to start validating your Excel workbooks. Need help? Click the help icon in the header anytime.',
          target: '.dashboard-header',
          position: 'bottom'
        }
      ]
    };
  }

  /**
   * Start a tour
   */
  async startTour(tour: Tour) {
    // Set tour data but don't activate yet (to prevent modal from showing before scroll)
    this.currentTourSignal.set(tour);
    this.currentStepIndexSignal.set(0);
    
    // Navigate to first step's route if specified
    const firstStep = tour.steps[0];
    if (firstStep.route) {
      await this.router.navigate([firstStep.route]);
    }

    // Wait for navigation and DOM to be ready
    await this.delay(300);
    
    // Scroll to target element first
    await this.scrollToTargetAsync(firstStep.target);
    
    // Wait for scroll to complete
    await this.delay(400);
    
    // Now activate the tour (show modal)
    this.isActiveSignal.set(true);
    
    // Execute any step action
    firstStep.action?.();
  }

  /**
   * Go to next step
   */
  async nextStep() {
    const tour = this.currentTourSignal();
    if (!tour) return;

    const nextIndex = this.currentStepIndexSignal() + 1;

    if (nextIndex >= tour.steps.length) {
      this.completeTour();
      return;
    }

    // Hide modal during transition
    this.isActiveSignal.set(false);
    
    this.currentStepIndexSignal.set(nextIndex);
    const step = tour.steps[nextIndex];

    // Navigate if route specified
    if (step.route) {
      await this.router.navigate([step.route]);
      await this.delay(300);
    }

    // Scroll to target first
    await this.scrollToTargetAsync(step.target);
    await this.delay(400);
    
    // Show modal after scroll completes
    this.isActiveSignal.set(true);
    
    // Execute step action
    step.action?.();
  }

  /**
   * Go to previous step
   */
  async previousStep() {
    const currentIndex = this.currentStepIndexSignal();
    if (currentIndex === 0) return;

    const prevIndex = currentIndex - 1;
    
    // Hide modal during transition
    this.isActiveSignal.set(false);
    
    this.currentStepIndexSignal.set(prevIndex);

    const tour = this.currentTourSignal();
    if (!tour) return;

    const step = tour.steps[prevIndex];

    // Navigate if route specified
    if (step.route) {
      await this.router.navigate([step.route]);
      await this.delay(300);
    }

    // Scroll to target first
    await this.scrollToTargetAsync(step.target);
    await this.delay(400);
    
    // Show modal after scroll completes
    this.isActiveSignal.set(true);
    
    // Execute step action
    step.action?.();
  }

  /**
   * Skip the current tour
   */
  skipTour() {
    const tour = this.currentTourSignal();
    if (tour) {
      this.markTourSkipped(tour.id);
    }
    this.endTour();
  }

  /**
   * Complete the current tour
   */
  completeTour() {
    const tour = this.currentTourSignal();
    if (tour) {
      this.markTourCompleted(tour.id);
    }
    this.endTour();
  }

  /**
   * End tour without marking complete
   */
  private endTour() {
    this.currentTourSignal.set(null);
    this.currentStepIndexSignal.set(0);
    this.isActiveSignal.set(false);
  }

  /**
   * Check if user has completed a tour
   */
  isTourCompleted(tourId: string): boolean {
    const completed = this.getCompletedTours();
    return completed.includes(tourId);
  }

  /**
   * Check if user has skipped the tour
   */
  isTourSkipped(tourId: string): boolean {
    const skipped = this.getSkippedTours();
    return skipped.includes(tourId);
  }

  /**
   * Check if user should see tour (not completed and not skipped)
   */
  shouldShowTour(tourId: string): boolean {
    return !this.isTourCompleted(tourId) && !this.isTourSkipped(tourId);
  }

  /**
   * Reset tour status (for testing or user preference)
   */
  resetTour(tourId: string) {
    const completed = this.getCompletedTours().filter(id => id !== tourId);
    const skipped = this.getSkippedTours().filter(id => id !== tourId);
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(completed));
    localStorage.setItem(this.TOUR_SKIPPED_KEY, JSON.stringify(skipped));
  }

  /**
   * Reset all tours
   */
  resetAllTours() {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.TOUR_SKIPPED_KEY);
  }

  // Private helper methods

  /**
   * Async delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Scroll to target element and wait for scroll to complete
   */
  private async scrollToTargetAsync(selector: string): Promise<void> {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private markTourCompleted(tourId: string) {
    const completed = this.getCompletedTours();
    if (!completed.includes(tourId)) {
      completed.push(tourId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(completed));
    }
  }

  private markTourSkipped(tourId: string) {
    const skipped = this.getSkippedTours();
    if (!skipped.includes(tourId)) {
      skipped.push(tourId);
      localStorage.setItem(this.TOUR_SKIPPED_KEY, JSON.stringify(skipped));
    }
  }

  private getCompletedTours(): string[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  private getSkippedTours(): string[] {
    const stored = localStorage.getItem(this.TOUR_SKIPPED_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  private scrollToTarget(selector: string) {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}
